/**
 * @package FlorenceEGI — EGI — Home Supreme Collections Grid renderer
 * @author Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version 2.0.0
 * @date 2026-04-22
 * @purpose Island 5 — grid editoriale collezioni. Card = mosaic 2×2 thumbs
 *          (FEPlate fallback per slot vuoto), nome italica, count oro.
 *          Consuma /api/home-supreme/featured-collections.
 */

import { el, clear, lazyImg, buildSrcset, pickVariant } from "./utils";
import type { ImageVariants } from "./utils";
import { buildPlate, pickTone } from "./plate";

type CollectionDto = {
    id: number;
    name: string;
    egis_count: number;
    creator: { id: number; name: string; nick_name: string | null } | null;
    thumbs: string[];
    thumbs_image?: ImageVariants[];
    url: string;
};

function mosaicTile(
    variants: ImageVariants | undefined,
    fallbackSrc: string | undefined,
    seed: string,
    alt: string,
): HTMLElement {
    const tile = el("span");
    if (variants || fallbackSrc) {
        // Mosaic tile ~150×150 (4 tiles in una card piccola) → thumbnail.
        const best = pickVariant(variants, 150) ?? fallbackSrc ?? "";
        const srcset = buildSrcset(variants);
        const img = lazyImg(best, alt, {
            width: 200, height: 200,
            sizes: "(min-width:900px) 12vw, 40vw",
            ...(srcset ? { srcset } : {}),
        });
        tile.appendChild(img);
    } else {
        const plate = buildPlate(seed, pickTone(seed));
        tile.appendChild(plate);
    }
    return tile;
}

function card(c: CollectionDto): HTMLElement {
    const a = el("a", {
        className: "fe-collection-card",
        attrs: { href: c.url, "aria-label": c.name },
    });

    const mosaic = el("div", { className: "fe-collection-card__mosaic", attrs: { "aria-hidden": "true" } });
    for (let i = 0; i < 4; i++) {
        const variants = c.thumbs_image?.[i];
        const fallback = c.thumbs[i];
        mosaic.appendChild(mosaicTile(variants, fallback, `coll-${c.id}-${i}`, `${c.name} ${i + 1}`));
    }
    a.appendChild(mosaic);

    const body = el("div", { className: "fe-collection-card__body" });
    body.appendChild(el("h3", { className: "fe-collection-card__name", text: c.name }));

    const meta = el("div", { className: "fe-collection-card__meta" });
    meta.appendChild(el("span", {
        text: c.creator ? (c.creator.nick_name ?? c.creator.name) : "—",
    }));
    meta.appendChild(el("span", {
        className: "fe-collection-card__count",
        text: `${c.egis_count} opere`,
    }));
    body.appendChild(meta);
    a.appendChild(body);
    return a;
}

export function renderCollectionsGrid(host: HTMLElement, data: Record<string, unknown>): void {
    clear(host);
    const items = Array.isArray((data as { items?: CollectionDto[] }).items)
        ? (data as { items: CollectionDto[] }).items
        : [];
    if (!items.length) {
        host.appendChild(el("p", { className: "empty", text: "—" }));
        return;
    }
    const grid = el("div", { className: "fe-collections", attrs: { role: "list" } });
    for (const c of items) {
        const cc = card(c);
        cc.setAttribute("role", "listitem");
        grid.appendChild(cc);
    }
    host.appendChild(grid);
}
